import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { settingsAPI } from '../services/api';

const ChatWidget = () => {
  const { language } = useLanguage();
  const tawkLoadedRef = useRef(false);
  const [chatEnabled, setChatEnabled] = useState(true);

  useEffect(() => {
    // Fetch chat enabled setting from backend
    const fetchChatSettings = async () => {
      try {
        const settings = await settingsAPI.getAll();
        setChatEnabled(settings.chat_enabled === 'true' || settings.chat_enabled === true);
      } catch (error) {
        console.error('Error fetching chat settings:', error);
        // Default to enabled if fetch fails
        setChatEnabled(true);
      }
    };

    fetchChatSettings();
  }, []);

  useEffect(() => {
    // Only load if chat is enabled
    if (!chatEnabled) {
      // Remove Tawk if it was previously loaded
      if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
        window.Tawk_API.hideWidget();
      }
      return;
    }

    // Prevent multiple loads
    if (tawkLoadedRef.current) {
      // If already loaded, just show it
      if (window.Tawk_API && typeof window.Tawk_API.showWidget === 'function') {
        window.Tawk_API.showWidget();
      }
      return;
    }

    // Get credentials from environment variables
    const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
    const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID;

    if (!propertyId || !widgetId) {
      console.warn('Tawk.to credentials not configured. Please set REACT_APP_TAWK_PROPERTY_ID and REACT_APP_TAWK_WIDGET_ID in your .env file.');
      console.info('To get these credentials:');
      console.info('1. Sign up at https://www.tawk.to/ (FREE)');
      console.info('2. Go to Administration > Channels > Chat Widget');
      console.info('3. Copy your Property ID and Widget ID from the widget code');
      return;
    }

    // Initialize Tawk global object
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    
    window.Tawk_API.onLoad = function() {
      tawkLoadedRef.current = true;
      console.log('Tawk.to widget loaded successfully');

      // Add language attribute for better support
      if (window.Tawk_API.setAttributes) {
        window.Tawk_API.setAttributes({
          language: language
        }, function(error) {
          if (error) console.error('Error setting language attribute:', error);
        });
      }

      // Add custom event tracking
      if (window.Tawk_API.addEvent) {
        window.Tawk_API.addEvent('widget_loaded', {
          language: language,
          timestamp: new Date().toISOString()
        }, function(error) {
          if (error) console.error('Error adding event:', error);
        });
      }
    };

    // Load the Tawk script
    const script = document.createElement('script');
    script.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    script.async = true;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    document.body.appendChild(script);

    return () => {
      // Cleanup - hide widget but don't remove script
      if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
        window.Tawk_API.hideWidget();
      }
    };
  }, [chatEnabled, language]);

  // Handle language changes
  useEffect(() => {
    if (window.Tawk_API && tawkLoadedRef.current && window.Tawk_API.setAttributes) {
      window.Tawk_API.setAttributes({
        language: language
      }, function(error) {
        if (error) console.error('Error updating language attribute:', error);
      });
    }
  }, [language]);

  // This component doesn't render anything - widget loads globally
  return null;
};

export default ChatWidget;
